import * as tc from "@/features/Core/types/checkers";
import { isEdgeProvenance } from "@/features/Evidence/types/checkers";
import { ResultEdge, ResultNode, Path, EntityTags, Annotation, ChemicalAnnotation,
  DiseaseAnnotation, GeneAnnotation, EdgeMetadata } from "./results";

export const isSpecies = tc.makeIsOneOf(["Zebrafish", "Mouse", "Rat"] as const);
const _isTdl = tc.makeIsOneOf(["Tclin", "Tchem", "Tbio", "Tdark"] as const);
const _isTdlArray = tc.makeIsHomogeneousArray(_isTdl);

const _isAnnotationSource = (e: unknown): boolean =>
  tc.isObject(e) && tc.isString(e.id) && tc.isString(e.url)
  && (e.name === undefined || tc.isString(e.name));

const _isCurieEntry = (e: unknown): boolean =>
  tc.isObject(e) && tc.isString(e.curie) && tc.nullable(e.url, tc.isString);
const _isCurieEntryArray = tc.makeIsHomogeneousArray(_isCurieEntry);

const _isAnnotationSection = (isValue: (v: unknown) => boolean) => (e: unknown): boolean =>
  tc.isObject(e)
  && isValue(e.value)
  && tc.isObject(e.metadata)
  && tc.nullable(e.metadata.sources, tc.makeIsHomogeneousArray(_isAnnotationSource));


/**
 * Type guard to check if an object is a ResultEdge object.
 *
 * @param {unknown} obj - The object to check.
 * @param {boolean} warn - Whether to warn if the object is not a ResultEdge.
 * @returns {boolean} - True if the object is a ResultEdge, otherwise false.
 */
export const isResultEdge = (obj: unknown, warn = false): obj is ResultEdge => {
  if (!tc.isObject(obj)) {
    if (warn) console.warn("[isResultEdge] expected object, got:", typeof obj, obj);
    return false;
  }
  return tc.checkProperties("isResultEdge", obj, [
    ["aras", tc.isStringArray(obj.aras), "string[]", obj.aras],
    ["description", tc.missable(obj.description, tc.isString), "string | undefined | null", obj.description],
    ["id", tc.isString(obj.id), "string", obj.id],
    ["is_root", tc.isBoolean(obj.is_root), "boolean", obj.is_root],
    ["knowledge_level", tc.isString(obj.knowledge_level), "string", obj.knowledge_level],
    ["metadata", __isEdgeMetadata(obj.metadata, warn), "EdgeMetadata", obj.metadata],
    ["object", tc.isString(obj.object), "string", obj.object],
    ["predicate", tc.isString(obj.predicate), "string", obj.predicate],
    ["predicate_url", tc.isString(obj.predicate_url), "string", obj.predicate_url],
    ["provenance", tc.makeIsHomogeneousArray((p: unknown) => isEdgeProvenance(p, warn))(obj.provenance), "EdgeProvenance[]", obj.provenance],
    ["publications", tc.isObject(obj.publications), "object", obj.publications],
    ["subject", tc.isString(obj.subject), "string", obj.subject],
  ], warn);

  function __isEdgeMetadata(obj: unknown, warn = false): obj is EdgeMetadata {
    if (!tc.isObject(obj)) {
      if (warn) console.warn("[isResultEdge.metadata] expected object, got:", typeof obj, obj);
      return false;
    }
    return tc.checkProperties("isResultEdge.metadata", obj, [
      ["edge_bindings", tc.isStringArray(obj.edge_bindings), "string[]", obj.edge_bindings],
      ["inverted_id", tc.nullable(obj.inverted_id, tc.isString), "string | null", obj.inverted_id],
      ["is_root", tc.isBoolean(obj.is_root), "boolean", obj.is_root],
    ], warn);
  }
}

/**
 * Type guard to check if an object is a ResultNode object.
 *
 * @param {unknown} obj - The object to check.
 * @param {boolean} warn - Whether to warn if the object is not a ResultNode.
 * @returns {boolean} - True if the object is a ResultNode, otherwise false.
 */
export const isResultNode = (obj: unknown, warn = false): obj is ResultNode => {
  if (!tc.isObject(obj)) {
    if (warn) console.warn("[isResultNode] expected object, got:", typeof obj, obj);
    return false;
  }
  return tc.checkProperties("isResultNode", obj, [
    ["annotations", __isAnnotation(obj.annotations, warn), "Annotation", obj.annotations],
    ["aras", tc.isStringArray(obj.aras), "string[]", obj.aras],
    ["curies", tc.isStringArray(obj.curies), "string[]", obj.curies],
    ["descriptions", tc.isStringArray(obj.descriptions), "string[]", obj.descriptions],
    ["id", tc.isString(obj.id), "string", obj.id],
    ["names", tc.isStringArray(obj.names), "string[]", obj.names],
    ["provenance", tc.isStringArray(obj.provenance), "string[]", obj.provenance],
    ["synonyms", tc.isStringArray(obj.synonyms), "string[]", obj.synonyms],
    ["types", tc.isStringArray(obj.types), "string[]", obj.types],
  ], warn);

  /**
   * Type guard to check if an object is an Annotation object.
   *
   * @param {unknown} obj - The object to check.
   * @param {boolean} warn - Whether to warn if the object is not an Annotation.
   * @returns {boolean} - True if the object is an Annotation, otherwise false.
   */
  function __isAnnotation(obj: unknown, warn = false): obj is Annotation {
    if (!tc.isObject(obj)) {
      if (warn) console.warn("[isResultNode.annotations] expected object, got:", typeof obj, obj);
      return false;
    }
    return tc.checkProperties("isResultNode.annotations", obj, [
      ["chemical", __isChemicalAnnotation(obj.chemical, warn), "ChemicalAnnotation", obj.chemical],
      ["disease", __isDiseaseAnnotation(obj.disease, warn), "DiseaseAnnotation", obj.disease],
      ["gene", __isGeneAnnotation(obj.gene, warn), "GeneAnnotation", obj.gene],
    ], warn);
  }

  /**
   * Type guard to check if an object is a ChemicalAnnotation object.
   *
   * @param {unknown} obj - The object to check.
   * @param {boolean} warn - Whether to warn if the object is not a ChemicalAnnotation.
   * @returns {boolean} - True if the object is a ChemicalAnnotation, otherwise false.
   */
  function __isChemicalAnnotation(obj: unknown, warn = false): obj is ChemicalAnnotation {
    if (!tc.isObject(obj)) {
      if (warn) console.warn("[isResultNode.annotations.chemical] expected object, got:", typeof obj, obj);
      return false;
    }
    return tc.checkProperties("isResultNode.annotations.chemical", obj, [
      ["approval", tc.nullable(obj.approval, _isAnnotationSection(tc.isNumber)), "AnnotationSection<number> | null", obj.approval],
      ["clinical_trials", tc.nullable(obj.clinical_trials, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.clinical_trials],
      ["descriptions", tc.nullable(obj.descriptions, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.descriptions],
      ["curies", tc.nullable(obj.curies, _isAnnotationSection(_isCurieEntryArray)), "AnnotationSection<CurieEntry[]> | null", obj.curies],
      ["indications", tc.nullable(obj.indications, _isAnnotationSection(tc.makeIsHomogeneousArray(e => tc.isObject(e) && tc.isString(e.name) && tc.isStringArray(e.ids) && tc.isStringArray(e.urls)))), "AnnotationSection<Indication[]> | null", obj.indications],
      ["otc_status", tc.nullable(obj.otc_status, _isAnnotationSection((e) => tc.isObject(e) && tc.isNumber(e.code) && tc.isString(e.label))), "AnnotationSection<{code, label}> | null", obj.otc_status],
      ["roles", tc.nullable(obj.roles, _isAnnotationSection(tc.makeIsHomogeneousArray(e => tc.isObject(e) && tc.isString(e.id) && tc.isString(e.name)))), "AnnotationSection<ChebiRole[]> | null", obj.roles],
      ["synonyms", tc.nullable(obj.synonyms, _isAnnotationSection(e => tc.isObject(e) && tc.isStringArray(e.commercial) && tc.isStringArray(e.generic))), "AnnotationSection<{commercial, generic}> | null", obj.synonyms],
    ], warn);
  }

  /**
   * Type guard to check if an object is a DiseaseAnnotation object.
   *
   * @param {unknown} obj - The object to check.
   * @param {boolean} warn - Whether to warn if the object is not a DiseaseAnnotation.
   * @returns {boolean} - True if the object is a DiseaseAnnotation, otherwise false.
   */
  function __isDiseaseAnnotation(obj: unknown, warn = false): obj is DiseaseAnnotation {
    if (!tc.isObject(obj)) {
      if (warn) console.warn("[isResultNode.annotations.disease] expected object, got:", typeof obj, obj);
      return false;
    }
    return tc.checkProperties("isResultNode.annotations.disease", obj, [
      ["clinical_trials", tc.nullable(obj.clinical_trials, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.clinical_trials],
      ["curies", tc.nullable(obj.curies, _isAnnotationSection(_isCurieEntryArray)), "AnnotationSection<CurieEntry[]> | null", obj.curies],
      ["descriptions", tc.nullable(obj.descriptions, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.descriptions],
      ["synonyms", tc.nullable(obj.synonyms, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.synonyms],
    ], warn);
  }

  /**
   * Type guard to check if an object is a GeneAnnotation object.
   *
   * @param {unknown} obj - The object to check.
   * @param {boolean} warn - Whether to warn if the object is not a GeneAnnotation.
   * @returns {boolean} - True if the object is a GeneAnnotation, otherwise false.
   */
  function __isGeneAnnotation(obj: unknown, warn = false): obj is GeneAnnotation {
    if (!tc.isObject(obj)) {
      if (warn) console.warn("[isResultNode.annotations.gene] expected object, got:", typeof obj, obj);
      return false;
    }
    return tc.checkProperties("isResultNode.annotations.gene", obj, [
      ["descriptions", tc.nullable(obj.descriptions, _isAnnotationSection(tc.isStringArray)), "AnnotationSection<string[]> | null", obj.descriptions],
      ["curies", tc.nullable(obj.curies, _isAnnotationSection(_isCurieEntryArray)), "AnnotationSection<CurieEntry[]> | null", obj.curies],
      ["name", tc.nullable(obj.name, _isAnnotationSection(tc.isString)), "AnnotationSection<string> | null", obj.name],
      ["species", tc.nullable(obj.species, _isAnnotationSection(isSpecies)), "AnnotationSection<Species> | null", obj.species],
      ["tdl", tc.nullable(obj.tdl, _isAnnotationSection(_isTdlArray)), "AnnotationSection<Tdl[]> | null", obj.tdl],
    ], warn);
  }
}

/**
 * Type guard to check if an object is a Path object.
 *
 * @param {unknown} obj - The object to check.
 * @param {boolean} warn - Whether to warn if the object is not a Path.
 * @returns {boolean} - True if the object is a Path, otherwise false.
 */
export const isPath = (obj: unknown, warn = false): obj is Path => {
  if (!tc.isObject(obj)) {
    if (warn) console.warn("[isPath] expected object, got:", typeof obj, obj);
    return false;
  }
  return tc.checkProperties("isPath", obj, [
    ["aras", tc.isStringArray(obj.aras), "string[]", obj.aras],
    ["compressedIDs", tc.missable(obj.compressedIDs, tc.isStringArray), "string[] | undefined | null", obj.compressedIDs],
    ["compressedSubgraph", tc.missable(obj.compressedSubgraph, tc.makeIsHomogeneousArray((e: unknown) => tc.isString(e) || tc.isStringArray(e))), "(string | string[])[] | undefined | null", obj.compressedSubgraph],
    ["id", tc.missable(obj.id, tc.isString), "string | undefined | null", obj.id],
    ["score", tc.missable(obj.score, tc.isNumber), "number | undefined | null", obj.score],
    ["subgraph", tc.isStringArray(obj.subgraph), "string[]", obj.subgraph],
    ["tags", isTags(obj.tags, warn), "Tags", obj.tags],
  ], warn);
}

/**
 * Type guard to check if an object is a Tags object.
 *
 * @param {unknown} obj - The object to check.
 * @param {boolean} warn - Whether to warn if the object is not a Tags.
 * @returns {boolean} - True if the object is a Tags, otherwise false.
 */
export const isTags = (obj: unknown, warn = false): obj is EntityTags => {
  if (!tc.isObject(obj)) {
    if (warn) console.warn("[isTags] expected object, got:", typeof obj, obj);
    return false;
  }
  for (const key in obj) {
    const tag = (obj as Record<string, unknown>)[key];
    if (!tc.isObject(tag) || !tc.isString(tag.id) || !tc.isObject(tag.description)
      || !tc.isString(tag.description.name) || !tc.isString(tag.description.description)) {
      if (warn) console.warn(`[isTags] invalid tag at key "${key}": expected TagObject, got:`, tag);
      return false;
    }
  }
  return true;
};
